/**
 * dw-carousel-analytics.js
 * -------------------------------------------------------------------------
 * Divot Works — PDP image carousel engagement tracking.
 *
 * Measures how far through the product image gallery visitors get, how long
 * they spend on each image, and which image they were looking at when they
 * left the page.
 *
 * Destinations (all optional, see CONFIG):
 *   - window.dataLayer  -> Google Tag Manager (GTM-T4N2HWMR)
 *   - window.gtag       -> GA4 (G-QV078EVJTS) and Google Ads (AW-18189871287)
 *   - window.fbq        -> Meta pixel (1558014252059890)
 *   - Shopify.analytics.publish -> Shopify Web Pixel layer (custom pixel)
 *
 * Events emitted:
 *   pdp_carousel_image_view  every time a new image becomes the active one
 *   pdp_carousel_milestone   first time 25 / 50 / 75 / 100% depth is reached
 *   pdp_carousel_depth       once on page exit — the drop-off / exit-image event
 *
 * Design notes:
 *   - Purely observational. It never calls into the theme's gallery code and
 *     never mutates the DOM, so it cannot break carousel behaviour.
 *   - Desktop (thumbnail_slider layout) changes images by toggling the
 *     `is-active` class; mobile swipe fires a `slideChanged` event. Both are
 *     watched, plus an IntersectionObserver fallback for stacked layouts.
 *   - The gallery <li> list is re-ordered in place on variant change
 *     (product-info.js updateMedia), so the slide list is recomputed whenever
 *     the list's children change.
 * -------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = {
    // --- destinations -----------------------------------------------------
    sendToDataLayer: true, // GTM. Always safe to leave on.
    sendDirectToGa4: true, // Set to false if you build a GA4 tag in GTM instead.
    sendDirectToMeta: true, // Set to false if you build a Meta tag in GTM instead.
    sendToShopifyPixel: true,

    ga4MeasurementId: 'G-QV078EVJTS',
    googleAdsId: 'AW-18189871287',

    // Optional Google Ads conversion labels. Leave blank unless you have
    // created a conversion action in the Google Ads UI and want to fire it
    // directly. Recommended route is instead to import the GA4 event.
    // Format: 'AW-18189871287/AbCdEfGhIjKl'
    adsLabelMilestone100: '',
    adsLabelEngaged: '',

    // --- behaviour --------------------------------------------------------
    milestones: [25, 50, 75, 100],
    minSlidesToTrack: 2, // don't track single-image galleries
    maxDwellMs: 300000, // ignore dwell longer than 5 min (idle tab safety net)
    debug: false // or append ?dw_debug=1 to the URL
  };

  // -----------------------------------------------------------------------
  // Boot
  // -----------------------------------------------------------------------

  var DEBUG =
    CONFIG.debug ||
    (typeof window.location !== 'undefined' && window.location.search.indexOf('dw_debug=1') !== -1);

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('%c[dw-carousel]', 'color:#54E5B0;font-weight:bold');
    window.console.log.apply(window.console, args);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    // Give the theme's custom elements a tick to upgrade.
    window.setTimeout(init, 0);
  });

  function init() {
    var galleries = document.querySelectorAll('media-gallery');
    var gallery = null;

    // Prefer the main product section's gallery.
    for (var i = 0; i < galleries.length; i++) {
      if ((galleries[i].id || '').indexOf('__main') !== -1) {
        gallery = galleries[i];
        break;
      }
    }
    if (!gallery && galleries.length) gallery = galleries[0];
    if (!gallery) return log('no media-gallery on this page — nothing to track');

    var list = gallery.querySelector('[id^="Slider-Gallery"]');
    if (!list) list = gallery.querySelector('ul');
    if (!list) return log('no slide list found');

    var viewer = gallery.querySelector('[id^="GalleryViewer"]') || list.parentElement;

    new CarouselTracker(gallery, list, viewer).start();
  }

  // -----------------------------------------------------------------------
  // Tracker
  // -----------------------------------------------------------------------

  function CarouselTracker(gallery, list, viewer) {
    this.gallery = gallery;
    this.list = list;
    this.viewer = viewer;

    this.slides = [];
    this.currentIndex = -1; // 0-based
    this.maxIndex = 0;
    this.viewed = {}; // index -> true
    this.dwell = {}; // index -> accumulated ms
    this.milestonesFired = {};
    this.reorderCount = 0;
    this.slideEnteredAt = 0;
    this.startedAt = now();
    this.depthSent = false;
    this.depthSignature = '';
    this.sessionId = makeId();

    this.product = readProductContext();
  }

  CarouselTracker.prototype.start = function () {
    this.refreshSlides();

    if (this.slides.length < CONFIG.minSlidesToTrack) {
      return log('only ' + this.slides.length + ' slide(s) — not tracking');
    }

    log('tracking gallery', this.gallery.id, this.slides.length + ' slides', this.product);

    var self = this;

    // 1) Desktop thumbnail clicks + variant changes toggle `is-active`,
    //    and variant changes add/remove/reorder <li> elements.
    this.domObserver = new MutationObserver(function (mutations) {
      var structural = false;
      var activeEl = null;

      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          structural = true;
        } else if (
          m.type === 'attributes' &&
          m.target &&
          m.target.classList &&
          m.target.classList.contains('is-active')
        ) {
          activeEl = m.target;
        }
      }

      if (structural) {
        self.reorderCount++;
        self.refreshSlides();
        activeEl = activeEl || self.list.querySelector('li.is-active[data-media-id]');
      }
      if (activeEl) self.setActiveElement(activeEl, 'class');
    });

    this.domObserver.observe(this.list, {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: false
    });

    // Observe attributes on the direct children too (subtree:false above only
    // covers the list itself for childList; attributes need per-node observing).
    this.observeSlideAttributes();

    // 2) Mobile swipe / slider movement.
    if (this.viewer && this.viewer.addEventListener) {
      this.viewer.addEventListener('slideChanged', function (event) {
        var el =
          (event.detail && event.detail.currentElement) ||
          (event.detail && typeof event.detail.currentPage === 'number'
            ? self.slides[event.detail.currentPage - 1]
            : null);
        if (el) self.setActiveElement(el, 'slide');
      });
    }

    // 3) Fallback for stacked layouts where neither of the above fires.
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        function (entries) {
          if (self.sawExplicitSignal) return;
          var best = null;
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && (!best || entries[i].intersectionRatio > best.intersectionRatio)) {
              best = entries[i];
            }
          }
          if (best && best.intersectionRatio >= 0.5) self.setActiveElement(best.target, 'scroll');
        },
        { threshold: [0.5] }
      );
      this.observeSlideVisibility();
    }

    // 4) Exit / backgrounding.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        self.accumulateDwell();
        self.sendDepth('hidden');
      } else {
        self.slideEnteredAt = now();
      }
    });
    window.addEventListener('pagehide', function () {
      self.accumulateDwell();
      self.sendDepth('pagehide');
    });

    // Seed with whatever is active on load.
    var initial =
      this.list.querySelector('li.is-active[data-media-id]') || this.slides[0] || null;
    if (initial) this.setActiveElement(initial, 'initial');
  };

  CarouselTracker.prototype.refreshSlides = function () {
    var found = this.list.querySelectorAll(':scope > li[data-media-id]');
    if (!found.length) found = this.list.querySelectorAll('li[data-media-id]');
    this.slides = Array.prototype.slice.call(found);
    this.observeSlideAttributes();
    this.observeSlideVisibility();
  };

  CarouselTracker.prototype.observeSlideAttributes = function () {
    if (!this.domObserver) return;
    var self = this;
    this.slides.forEach(function (li) {
      if (li.__dwObserved) return;
      li.__dwObserved = true;
      self.domObserver.observe(li, { attributes: true, attributeFilter: ['class'] });
    });
  };

  CarouselTracker.prototype.observeSlideVisibility = function () {
    if (!this.io) return;
    var self = this;
    this.slides.forEach(function (li) {
      if (li.__dwIo) return;
      li.__dwIo = true;
      self.io.observe(li);
    });
  };

  CarouselTracker.prototype.setActiveElement = function (el, source) {
    if (!el) return;
    var index = this.slides.indexOf(el);
    if (index === -1) {
      this.refreshSlides();
      index = this.slides.indexOf(el);
    }
    if (index === -1) return;
    if (source === 'class' || source === 'slide') this.sawExplicitSignal = true;
    this.setIndex(index, source);
  };

  CarouselTracker.prototype.setIndex = function (index, source) {
    if (index === this.currentIndex) return; // de-dupe repeated signals
    if (index < 0 || index >= this.slides.length) return;

    var prevIndex = this.currentIndex;
    var prevDwell = this.accumulateDwell();

    this.currentIndex = index;
    this.slideEnteredAt = now();

    var isFirstView = !this.viewed[index];
    this.viewed[index] = true;
    if (index > this.maxIndex) this.maxIndex = index;

    var total = this.slides.length;
    var human = index + 1;
    var percent = Math.round((human / total) * 100);
    var slide = this.slides[index];

    var payload = extend(this.baseParams(), {
      image_index: human,
      image_total: total,
      image_percent: percent,
      media_id: shortMediaId(slide),
      media_type: mediaTypeOf(slide),
      image_name: imageNameOf(slide),
      images_viewed: countKeys(this.viewed),
      max_image_index: this.maxIndex + 1,
      is_first_view: isFirstView ? 1 : 0,
      prev_image_index: prevIndex >= 0 ? prevIndex + 1 : 0,
      prev_image_dwell_ms: prevDwell
    });

    log('image_view', payload);

    // The initial image is one per pageview — send it to GA4 / GTM / Shopify as
    // the funnel denominator, but keep it out of Meta (ViewContent already
    // covers that) so the Meta event volume stays meaningful.
    this.emit('pdp_carousel_image_view', 'PDPCarouselImageView', payload, {
      skipMeta: source === 'initial'
    });

    this.checkMilestones(percent);
  };

  CarouselTracker.prototype.checkMilestones = function (percent) {
    var self = this;
    CONFIG.milestones.forEach(function (m) {
      if (percent < m || self.milestonesFired[m]) return;
      self.milestonesFired[m] = true;

      var payload = extend(self.baseParams(), {
        milestone: m,
        image_index: self.currentIndex + 1,
        image_total: self.slides.length,
        images_viewed: countKeys(self.viewed),
        time_to_milestone_ms: now() - self.startedAt
      });

      log('milestone', m, payload);
      self.emit('pdp_carousel_milestone', 'PDPCarouselMilestone', payload);

      if (m === 100 && CONFIG.adsLabelMilestone100) {
        fireAdsConversion(CONFIG.adsLabelMilestone100);
      }
      if (m === CONFIG.milestones[0] && CONFIG.adsLabelEngaged) {
        fireAdsConversion(CONFIG.adsLabelEngaged);
      }
    });
  };

  CarouselTracker.prototype.accumulateDwell = function () {
    if (this.currentIndex < 0 || !this.slideEnteredAt) return 0;
    var elapsed = now() - this.slideEnteredAt;
    if (elapsed < 0) elapsed = 0;
    if (elapsed > CONFIG.maxDwellMs) elapsed = CONFIG.maxDwellMs;
    this.dwell[this.currentIndex] = (this.dwell[this.currentIndex] || 0) + elapsed;
    this.slideEnteredAt = now();
    return elapsed;
  };

  CarouselTracker.prototype.sendDepth = function (reason) {
    var viewedCount = countKeys(this.viewed);
    var signature = this.maxIndex + ':' + viewedCount + ':' + this.currentIndex;

    // Only re-send if something actually changed since the last beacon
    // (handles tab-away, come back, browse more, leave).
    if (this.depthSent && signature === this.depthSignature) return;
    this.depthSent = true;
    this.depthSignature = signature;

    var total = this.slides.length;
    var lastSlide = this.slides[this.currentIndex] || null;

    var payload = extend(this.baseParams(), {
      max_image_index: this.maxIndex + 1,
      max_image_percent: Math.round(((this.maxIndex + 1) / total) * 100),
      image_total: total,
      images_viewed: viewedCount,
      last_image_index: this.currentIndex + 1,
      last_image_media_id: shortMediaId(lastSlide),
      last_image_name: imageNameOf(lastSlide),
      last_image_dwell_ms: this.dwell[this.currentIndex] || 0,
      carousel_total_time_ms: now() - this.startedAt,
      reached_end: this.maxIndex + 1 >= total ? 1 : 0,
      engaged: viewedCount > 1 ? 1 : 0,
      reorder_count: this.reorderCount,
      exit_reason: reason,
      dwell_map: this.dwellMap()
    });

    log('depth', payload);
    this.emit('pdp_carousel_depth', 'PDPCarouselDepth', payload, { beacon: true });
  };

  /** Compact "index:seconds" map, e.g. "1:12,2:3,3:41". Capped at 100 chars. */
  CarouselTracker.prototype.dwellMap = function () {
    var parts = [];
    for (var i = 0; i < this.slides.length; i++) {
      var ms = this.dwell[i];
      if (!ms) continue;
      parts.push(i + 1 + ':' + Math.round(ms / 1000));
    }
    var out = parts.join(',');
    return out.length > 100 ? out.slice(0, 97) + '...' : out;
  };

  CarouselTracker.prototype.baseParams = function () {
    return {
      carousel_session_id: this.sessionId,
      product_id: this.product.id,
      product_title: this.product.title,
      variant_id: currentVariantId(),
      page_location_path: window.location.pathname
    };
  };

  // -----------------------------------------------------------------------
  // Emit
  // -----------------------------------------------------------------------

  CarouselTracker.prototype.emit = function (name, metaName, params, opts) {
    opts = opts || {};

    if (CONFIG.sendToDataLayer && Array.isArray(window.dataLayer)) {
      try {
        window.dataLayer.push(extend({ event: name }, params));
      } catch (e) {
        log('dataLayer push failed', e);
      }
    }

    if (CONFIG.sendDirectToGa4 && typeof window.gtag === 'function' && CONFIG.ga4MeasurementId) {
      try {
        var gaParams = extend({ send_to: CONFIG.ga4MeasurementId }, params);
        if (opts.beacon) gaParams.transport_type = 'beacon';
        window.gtag('event', name, gaParams);
      } catch (e) {
        log('gtag failed', e);
      }
    }

    if (CONFIG.sendDirectToMeta && !opts.skipMeta && typeof window.fbq === 'function') {
      try {
        window.fbq('trackCustom', metaName, params);
      } catch (e) {
        log('fbq failed', e);
      }
    }

    if (
      CONFIG.sendToShopifyPixel &&
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        window.Shopify.analytics.publish('dw_' + name, params);
      } catch (e) {
        log('Shopify publish failed', e);
      }
    }
  };

  function fireAdsConversion(sendTo) {
    if (typeof window.gtag !== 'function' || !sendTo) return;
    try {
      window.gtag('event', 'conversion', { send_to: sendTo });
    } catch (e) {
      log('ads conversion failed', e);
    }
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  function now() {
    return window.performance && window.performance.now
      ? Math.round(window.performance.now())
      : new Date().getTime();
  }

  function makeId() {
    return (
      't' +
      new Date().getTime().toString(36) +
      Math.random().toString(36).slice(2, 8)
    );
  }

  function extend(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
      }
    }
    return target;
  }

  function countKeys(obj) {
    var n = 0;
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) n++;
    }
    return n;
  }

  function truncate(str, max) {
    str = String(str == null ? '' : str);
    return str.length > max ? str.slice(0, max - 1) + '…' : str;
  }

  /** "template--123__main-38352722690108" -> "38352722690108" */
  function shortMediaId(slide) {
    if (!slide || !slide.dataset || !slide.dataset.mediaId) return '';
    var parts = slide.dataset.mediaId.split('-');
    return parts[parts.length - 1];
  }

  function mediaTypeOf(slide) {
    if (!slide) return 'unknown';
    if (slide.querySelector('.product__media-video, video, iframe, deferred-media')) return 'video';
    if (slide.querySelector('model-viewer, .product__media-model')) return 'model';
    if (slide.querySelector('img')) return 'image';
    return 'other';
  }

  /** Prefer alt text (editorial, meaningful); fall back to the CDN filename. */
  function imageNameOf(slide) {
    if (!slide) return '';
    var img = slide.querySelector('img');
    if (!img) return '';
    var alt = (img.getAttribute('alt') || '').trim();
    if (alt) return truncate(alt, 100);
    var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
    try {
      var path = src.split('?')[0];
      return truncate(path.slice(path.lastIndexOf('/') + 1), 100);
    } catch (e) {
      return '';
    }
  }

  function readProductContext() {
    var meta =
      window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    var h1 = document.querySelector('.product__title h1, product-info h1, .product h1');
    var title = (h1 && h1.textContent ? h1.textContent : document.title).trim();
    return {
      id: meta && meta.id ? String(meta.id) : '',
      title: truncate(title, 100)
    };
  }

  function currentVariantId() {
    try {
      var input = document.querySelector(
        'product-info input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      if (input && input.value) return String(input.value);
      var params = new URLSearchParams(window.location.search);
      return params.get('variant') || '';
    } catch (e) {
      return '';
    }
  }
})();
